import { CheckCircle2, CreditCard, HelpCircle, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { getStorageBilling } from "../../lib/api";

const checklist = [
  "Cloudflare account created",
  "Payment method added",
  "Backup payment method added, if available",
  "Kreatethedate invited as collaborator",
  "Billing email confirmed",
  "Cloudflare login saved safely",
  "Personal backup of original photos/videos kept",
];

export function StorageBillingPage() {
  const [billingUrl, setBillingUrl] = useState("");
  const [fallbackMessage, setFallbackMessage] = useState(
    "Please log into your Cloudflare account and go to Billing to manage your payment method, invoices, and storage account.",
  );
  const [supportUrl, setSupportUrl] = useState("");

  useEffect(() => {
    void getStorageBilling().then((billing) => {
      setBillingUrl(billing.billingUrl || "");
      setFallbackMessage(billing.fallbackMessage);
      setSupportUrl(billing.supportRequestUrl || `mailto:${billing.supportEmail}`);
    });
  }, []);

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Storage Ownership & Billing"
        title="Keep Your Memory Media Vault Active"
        subtitle="Your photos, videos, guest uploads, and approved galleries stay connected through your client-owned cloud storage account."
      />

      <section className="grid gap-6 lg:grid-cols-[1fr_.8fr]">
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <CreditCard className="size-9 text-[#8a6c14]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Billing Reminder</h2>
          <p className="mt-4 leading-7 text-ktd-muted">
            To maintain access to your Memory Media Vault, please keep your Cloudflare billing and payment method
            current. Your storage account belongs to you, and Kreatethedate does not control your payment method,
            invoices, or billing access.
          </p>
          <p className="mt-4 leading-7 text-ktd-muted">
            Storage usage depends on the size and quantity of uploaded photos and videos. The guest upload link is not
            intended to be unlimited storage, and large videos or large upload batches may need additional planning.
          </p>
          <p className="mt-4 leading-7 text-ktd-muted">
            Additional storage, unusually large video collections, or extended support may require additional planning or
            fees depending on the client-owned setup.
          </p>
          {billingUrl ? (
            <a
              href={billingUrl}
              target="_blank"
              rel="noreferrer"
              className="focus-ring mt-6 inline-flex rounded-full bg-ktd-black px-6 py-3 text-sm font-extrabold text-ktd-softGold"
            >
              Manage Cloud Storage Billing
            </a>
          ) : (
            <p className="mt-6 rounded-lg bg-white p-4 text-sm font-semibold leading-6 text-ktd-muted">{fallbackMessage}</p>
          )}
        </article>

        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <ShieldCheck className="size-9 text-[#8a6c14]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Storage Ownership Checklist</h2>
          <div className="mt-5 grid gap-3">
            {checklist.map((item) => (
              <label key={item} className="flex items-center gap-3 rounded-lg bg-white p-3 text-sm font-bold text-ktd-rich">
                <input type="checkbox" className="size-4 accent-ktd-gold" />
                {item}
              </label>
            ))}
          </div>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <article className="rounded-lg bg-ktd-black p-6 text-white shadow-luxe sm:p-8">
          <CheckCircle2 className="size-9 text-ktd-softGold" />
          <h2 className="mt-5 font-display text-3xl font-bold">Important Notice</h2>
          <p className="mt-4 leading-7 text-white/68">
            Cloudflare billing is managed directly through your client-owned Cloudflare account. Kreatethedate provides
            setup, design, connection, and support while services are active, but the client is responsible for keeping
            their cloud storage account active.
          </p>
          <p className="mt-4 leading-7 text-white/68">
            Upload limits help keep the Memory Media Vault stable and comfortable for guests to use. Please keep a
            personal backup of original photos and videos outside the portal.
          </p>
          <p className="mt-4 rounded-lg border border-ktd-gold/20 bg-white/[0.05] p-4 text-sm leading-6 text-white/62">
            This Memory Media Vault is connected to a client-owned Cloudflare account. The client is responsible for
            keeping Cloudflare billing active, maintaining login access, and keeping a personal backup of all original
            photos and videos. Kreatethedate configures and manages the Memory Media Vault connection while services are active.
          </p>
        </article>
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <HelpCircle className="size-9 text-[#8a6c14]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Need Help Finding Your Billing Area?</h2>
          <p className="mt-4 leading-7 text-ktd-muted">
            Kreatethedate can guide you to the correct area, but only you can manage your payment method inside your own
            Cloudflare account.
          </p>
          {supportUrl && (
            <a
              href={supportUrl}
              className="focus-ring mt-6 inline-flex rounded-full bg-ktd-gold px-6 py-3 text-sm font-extrabold text-ktd-black"
            >
              Request Billing Guidance
            </a>
          )}
        </article>
      </section>
    </div>
  );
}
